import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Stack, Typography, Paper, alpha, useTheme,
  Chip, Skeleton, Button,
  Divider,
} from '@mui/material';
import {
  ReceiptLong, PointOfSale, Undo, Cancel as CancelIcon,
  History as HistoryIcon,
  AddCircleOutline, Redeem, LocalOffer,
} from '@mui/icons-material';
import { format } from 'date-fns';

import feePaymentService from '../../services/feePaymentService';
import InvoiceDetailDialog from '../fees/InvoiceDetailDialog';

import {
  FeeActivityEvent, FeeActivityKind,
} from '../../types/fees';
import ChallanNumberCell from '../fees/ChallanNumberCell';
import { useCurrencyFormat } from '../../hooks/useCurrencyFormat';
import { PAYMENT_METHOD_LABEL } from '../../utils/paymentMethods';

interface Props {
  studentId: string;
  refreshKey?: number;
}

/**
 * StudentFeeActivity — chronological feed of every fee-related event for
 * one student: invoice issued, invoice cancelled, payment received,
 * payment reversed.
 *
 * Kept as its own component so the parent Fees tab stays tight and this
 * only mounts when the section is on screen. Renders inline in the Fees
 * tab; not a separate route.
 */
const StudentFeeActivity: React.FC<Props> = ({ studentId, refreshKey }) => {
  const theme = useTheme();

  const INITIAL_LIMIT = 25;
  const EXPANDED_LIMIT = 100;

  const [events, setEvents] = useState<FeeActivityEvent[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [detailInvoiceId, setDetailInvoiceId] = useState<string | null>(null);

  const currencyFmt = useCurrencyFormat();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await feePaymentService.getStudentFeeActivity(
        studentId,
        expanded ? EXPANDED_LIMIT : INITIAL_LIMIT,
      );
      setEvents(data.events);
    } catch (e: any) {
      setError(e?.message || 'Failed to load activity');
    } finally {
      setLoading(false);
    }
  }, [studentId, expanded]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const kindStyle = (kind: FeeActivityKind) => {
    switch (kind) {
      case 'invoice_issued':
        return { color: theme.palette.info.main, dark: theme.palette.info.dark, icon: <ReceiptLong sx={{ fontSize: 14 }} /> };
      case 'invoice_cancelled':
        return { color: theme.palette.text.secondary, dark: theme.palette.text.secondary, icon: <CancelIcon sx={{ fontSize: 14 }} /> };
      case 'payment_received':
        return { color: theme.palette.success.main, dark: theme.palette.success.dark, icon: <PointOfSale sx={{ fontSize: 14 }} /> };
      case 'payment_reversed':
        return { color: theme.palette.error.main, dark: theme.palette.error.dark, icon: <Undo sx={{ fontSize: 14 }} /> };
      case 'adjustment_added':
        return { color: theme.palette.warning.main, dark: theme.palette.warning.dark, icon: <AddCircleOutline sx={{ fontSize: 14 }} /> };
      case 'adjustment_revoked':
        return { color: theme.palette.text.secondary, dark: theme.palette.text.secondary, icon: <Undo sx={{ fontSize: 14 }} /> };
      case 'waiver_applied':
        return { color: theme.palette.info.main, dark: theme.palette.info.dark, icon: <Redeem sx={{ fontSize: 14 }} /> };
      case 'waiver_revoked':
        return { color: theme.palette.text.secondary, dark: theme.palette.text.secondary, icon: <Undo sx={{ fontSize: 14 }} /> };
      case 'concession_created':
        return { color: theme.palette.primary.main, dark: theme.palette.primary.dark, icon: <LocalOffer sx={{ fontSize: 14 }} /> };
      case 'concession_revoked':
        return { color: theme.palette.text.secondary, dark: theme.palette.text.secondary, icon: <Undo sx={{ fontSize: 14 }} /> };
    }
  };

  // Human-readable label per adjustment kind. Backend enum stays technical.
  const adjustmentKindLabel: Record<string, string> = {
    late_fee: 'Late fee',
    ad_hoc_charge: 'Charge',
    correction: 'Correction',
    other: 'Adjustment',
  };

  const renderPrimary = (e: FeeActivityEvent) => {
    if (e.kind === 'invoice_issued' && e.invoice) {
      return (
        <>
          Challan{' '}
          <ChallanNumberCell value={e.invoice.invoiceNumber} fontWeight={700} />
          {' '}issued for{' '}
          <strong>{e.invoice.period.label}</strong>
          {' · '}
          <Typography component="span" variant="body2" fontWeight={700} sx={{ fontVariantNumeric: 'tabular-nums' }}>
            {currencyFmt(e.invoice.net)}
          </Typography>
        </>
      );
    }
    if (e.kind === 'invoice_cancelled' && e.invoice) {
      return (
        <>
          Challan{' '}
          <ChallanNumberCell value={e.invoice.invoiceNumber} fontWeight={700} />
          {' '}cancelled
        </>
      );
    }
    if (e.kind === 'payment_received' && e.payment) {
      return (
        <>
          Payment{' '}
          <Typography component="span" variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>
            {e.payment.receiptNumber}
          </Typography>
          {' · '}
          <Typography component="span" variant="body2" fontWeight={700} sx={{ fontVariantNumeric: 'tabular-nums', color: theme.palette.success.dark }}>
            {currencyFmt(e.payment.amount)}
          </Typography>
          {' via '}{PAYMENT_METHOD_LABEL[e.payment.method]}
        </>
      );
    }
    if (e.kind === 'payment_reversed' && e.payment) {
      return (
        <>
          Payment{' '}
          <Typography component="span" variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>
            {e.payment.receiptNumber}
          </Typography>
          {' '}reversed
        </>
      );
    }
    if (e.kind === 'adjustment_added' && e.adjustment) {
      const label = adjustmentKindLabel[e.adjustment.kind] ?? 'Adjustment';
      return (
        <>
          {label} of{' '}
          <Typography component="span" variant="body2" fontWeight={700} sx={{
            fontVariantNumeric: 'tabular-nums',
            color: e.adjustment.amount >= 0 ? theme.palette.warning.dark : theme.palette.info.dark,
          }}>
            {currencyFmt(e.adjustment.amount)}
          </Typography>
          {e.adjustment.invoiceNumber && (
            <>
              {' on '}
              <ChallanNumberCell value={e.adjustment.invoiceNumber} fontWeight={700} />
            </>
          )}
        </>
      );
    }
    if (e.kind === 'adjustment_revoked' && e.adjustment) {
      const label = adjustmentKindLabel[e.adjustment.kind] ?? 'Adjustment';
      return (
        <>
          {label} of{' '}
          <Typography component="span" variant="body2" fontWeight={700} sx={{ fontVariantNumeric: 'tabular-nums' }}>
            {currencyFmt(e.adjustment.amount)}
          </Typography>
          {' reversed'}
          {e.adjustment.invoiceNumber && (
            <>
              {' on '}
              <ChallanNumberCell value={e.adjustment.invoiceNumber} fontWeight={700} />
            </>
          )}
        </>
      );
    }
    if (e.kind === 'waiver_applied' && e.waiver) {
      const scope = e.waiver.lineItemIndex == null ? 'challan' : 'line';
      return (
        <>
          Waiver of{' '}
          <Typography component="span" variant="body2" fontWeight={700} sx={{
            fontVariantNumeric: 'tabular-nums',
            color: theme.palette.info.dark,
          }}>
            {currencyFmt(e.waiver.amount)}
          </Typography>
          {' applied to '}{scope}
          {e.waiver.invoiceNumber && (
            <>
              {' '}
              <ChallanNumberCell value={e.waiver.invoiceNumber} fontWeight={700} />
            </>
          )}
        </>
      );
    }
    if (e.kind === 'waiver_revoked' && e.waiver) {
      return (
        <>
          Waiver of{' '}
          <Typography component="span" variant="body2" fontWeight={700} sx={{ fontVariantNumeric: 'tabular-nums' }}>
            {currencyFmt(e.waiver.amount)}
          </Typography>
          {' revoked'}
          {e.waiver.invoiceNumber && (
            <>
              {' on '}
              <ChallanNumberCell value={e.waiver.invoiceNumber} fontWeight={700} />
            </>
          )}
        </>
      );
    }
    if (e.kind === 'concession_created' && e.concession) {
      const amountText = e.concession.mode === 'percent'
        ? `${e.concession.amount}%`
        : currencyFmt(e.concession.amount);
      const scopeText = e.concession.appliesTo === 'all' ? 'all categories' : 'specific categories';
      return (
        <>
          Concession <strong>{e.concession.label}</strong> added
          {' · '}
          <Typography component="span" variant="body2" fontWeight={700} sx={{ fontVariantNumeric: 'tabular-nums' }}>
            {amountText}
          </Typography>
          {' off '}{scopeText}
        </>
      );
    }
    if (e.kind === 'concession_revoked' && e.concession) {
      return (
        <>
          Concession <strong>{e.concession.label}</strong> revoked
        </>
      );
    }
    return null;
  };

  const renderSecondary = (e: FeeActivityEvent) => {
    const bits: React.ReactNode[] = [];
    if (e.reason) bits.push(<em key="r">Reason: {e.reason}</em>);
    if (e.kind === 'payment_received' && e.payment) {
      bits.push(`${e.payment.allocationCount} challan${e.payment.allocationCount === 1 ? '' : 's'} settled`);
      if (e.payment.paidBy) bits.push(`Paid by ${e.payment.paidBy.name}`);
      if (e.payment.referenceNumber) bits.push(<span key="ref" style={{ fontFamily: 'monospace' }}>{e.payment.referenceNumber}</span>);
    }
    if (bits.length === 0) return null;
    return (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
        {bits.map((b, i) => (
          <React.Fragment key={i}>
            {i > 0 && ' · '}{b}
          </React.Fragment>
        ))}
      </Typography>
    );
  };

  const grouped = useMemo(() => {
    if (!events) return [];
    // Group by day so the timeline reads as a chronology, not a wall of items.
    const map = new Map<string, FeeActivityEvent[]>();
    for (const e of events) {
      const key = format(new Date(e.at), 'yyyy-MM-dd');
      const arr = map.get(key) ?? [];
      arr.push(e);
      map.set(key, arr);
    }
    return Array.from(map.entries()).sort((a, b) => (a[0] < b[0] ? 1 : -1));
  }, [events]);

  if (loading && !events) {
    return (
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Stack spacing={1}>
          <Skeleton variant="text" width="30%" />
          <Skeleton variant="rectangular" height={60} sx={{ borderRadius: 1.5 }} />
          <Skeleton variant="rectangular" height={40} sx={{ borderRadius: 1.5 }} />
        </Stack>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, textAlign: 'center' }}>
        <Typography variant="caption" color="error.main">{error}</Typography>
        <Box sx={{ mt: 1 }}>
          <Button size="small" onClick={load}>Retry</Button>
        </Box>
      </Paper>
    );
  }

  if (!events || events.length === 0) {
    return null;   // parent tab already shows an empty state above us
  }

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{
          px: 2, py: 1.25,
          borderBottom: `1px solid ${theme.palette.divider}`,
          bgcolor: alpha(theme.palette.text.primary, 0.02),
        }}
      >
        <HistoryIcon fontSize="small" sx={{ color: 'text.secondary' }} />
        <Typography variant="body2" fontWeight={700}>Activity</Typography>
        <Chip
          size="small"
          label={events.length}
          sx={{ height: 20, fontSize: '0.68rem', fontWeight: 600 }}
        />
        <Box sx={{ flex: 1 }} />
        {!expanded && events.length >= INITIAL_LIMIT && (
          <Button size="small" onClick={() => setExpanded(true)}>
            Show more
          </Button>
        )}
      </Stack>

      <Box sx={{ px: 2, py: 1.5 }}>
        {grouped.map(([day, dayEvents], groupIdx) => (
          <Box key={day} sx={{ mb: groupIdx === grouped.length - 1 ? 0 : 2 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <Chip
                size="small"
                label={format(new Date(day), 'MMM d, yyyy')}
                variant="outlined"
                sx={{
                  height: 20, fontSize: '0.68rem', fontWeight: 600,
                  borderColor: theme.palette.divider,
                  color: 'text.secondary',
                }}
              />
              <Divider sx={{ flex: 1 }} />
            </Stack>

            {dayEvents.map((e, idx) => {
              const s = kindStyle(e.kind);
              const isLast = idx === dayEvents.length - 1;
              // Every event that references an invoice opens the invoice detail
              // on click. Concessions are recurring policies, not per-invoice.
              const linkedInvoiceId =
                (e.kind === 'invoice_issued' || e.kind === 'invoice_cancelled') ? e.invoice?._id
                : (e.kind === 'adjustment_added' || e.kind === 'adjustment_revoked') ? e.adjustment?.invoiceId
                : (e.kind === 'waiver_applied' || e.kind === 'waiver_revoked') ? e.waiver?.invoiceId
                : undefined;
              const clickable = Boolean(linkedInvoiceId);
              return (
                <Box
                  key={`${e.kind}-${idx}-${e.at}`}
                  onClick={clickable ? () => setDetailInvoiceId(linkedInvoiceId!) : undefined}
                  sx={{
                    position: 'relative',
                    pl: 3,
                    pb: isLast ? 0 : 2,
                    cursor: clickable ? 'pointer' : 'default',
                    ...(clickable && {
                      '&:hover .activity-primary': {
                        textDecoration: 'underline',
                        textDecorationColor: alpha(theme.palette.text.primary, 0.35),
                      },
                    }),
                  }}
                >
                  {!isLast && (
                    <Box sx={{
                      position: 'absolute',
                      left: 9, top: 18, bottom: -4, width: '1px',
                      bgcolor: theme.palette.divider,
                    }} />
                  )}
                  <Box sx={{
                    position: 'absolute',
                    left: 4, top: 4,
                    width: 12, height: 12, borderRadius: '50%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    bgcolor: alpha(s.color, 0.15),
                    color: s.dark,
                    border: `1px solid ${alpha(s.color, 0.4)}`,
                  }}>
                    {s.icon}
                  </Box>
                  <Box>
                    <Typography variant="body2" className="activity-primary">
                      {renderPrimary(e)}
                    </Typography>
                    <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.25 }}>
                      {format(new Date(e.at), 'p')}
                    </Typography>
                    {renderSecondary(e)}
                  </Box>
                </Box>
              );
            })}
          </Box>
        ))}

        {expanded && events.length >= EXPANDED_LIMIT && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
            Showing the most recent {EXPANDED_LIMIT} events. Older activity is preserved in the ledger.
          </Typography>
        )}
      </Box>

      <InvoiceDetailDialog
        open={Boolean(detailInvoiceId)}
        invoiceId={detailInvoiceId}
        onClose={() => setDetailInvoiceId(null)}
        onChanged={load}
      />
    </Paper>
  );
};

export default StudentFeeActivity;
